import {
  AppUser,
  AuthService,
  empty,
  EMPTY
} from '@app/components/login.component';
import { isJedinicaGradiva, JedinicaGradiva } from '../models/jedinica-gradiva';
import { isNerazvrstanoJedinica, isRootJedinica } from './common-utils';

function isDifferentInstitution(jedinice: JedinicaGradiva[], user: AppUser) {
  return jedinice.some(
    (item: JedinicaGradiva) =>
      item['en~jedinica_gradiva~posjednik'] !== user.maticnaInstitucijaId
  );
}

export function isDeleteActionDisabled(
  selectedItems: JedinicaGradiva[],
  user: AppUser | EMPTY
): boolean {
  if (user === empty) {
    return true;
  }

  const nerazvrstanoZbirka = isDisabledActionForNerazvrstano(selectedItems);
  const locked = isDisabledActionForLocked(selectedItems);

  if (nerazvrstanoZbirka || locked) {
    return true;
  }

  const selectedJedinice = selectedItems as JedinicaGradiva[];
  const isDifferent = isDifferentInstitution(selectedJedinice, user as AppUser);

  if (isDifferent) {
    return true; // TODO Ali cemo u nekom trenutku ipak morati dopustiti ako korisnik radi u ime druge institucije
  } else if (AuthService.userIsAdminOrVoditeljZbirki(user as AppUser)) {
    return false;
  } else {
    return selectedJedinice.some(
      (item: JedinicaGradiva) =>
        !(item['en~jedinica_gradiva~arhivist_na_obradi'] as number[]).includes(
          user.userId.value
        )
    );
  }
}

export function isDisabledActionForNerazvrstano(
  selectedItems: object[]
): boolean {
  return selectedItems.some((item: any) =>
    item['en~jedinica_gradiva~arhivska_oznaka']?.endsWith('Nerazvrstano-1')
  );
}

export function isDisabledActionForLocked(selectedItems: object[]): boolean {
  return selectedItems.some(
    (item: any) => item['en~jedinica_gradiva~locked'] as boolean
  );
}

export function isMoveActionDisabled(
  selectedItems: object[],
  user: AppUser | EMPTY
): { disabled: boolean; message: string } {
  if (selectedItems.length === 0 || user === empty) {
    return { disabled: true, message: '' };
  }

  if (isJedinicaGradiva(selectedItems[0])) {
    const selectedJedinice = selectedItems as JedinicaGradiva[];

    if (selectedJedinice.some((j) => j['en~jedinica_gradiva~locked'])) {
      return {
        disabled: true,
        message:
          'Odabrana jedinica je zaključana i nije ju moguće premjestiti. Otključajte jedinicu prije premještanja.'
      };
    }

    // FOND i ZBIRKA se ne smiju premještati (osim Nerazvrstano zbirke)
    if (
      selectedJedinice.some((j: any) => {
        return isRootJedinica(j) && !isNerazvrstanoJedinica(j);
      })
    ) {
      return {
        disabled: true,
        message: 'Premještanje fonda ili zbirke nije dozvoljeno.'
      };
    }

    // Batch provjera: sve selektirane jedinice moraju biti iste razine i iz istog parenta
    if (selectedJedinice.length > 1) {
      const firstRazina = (selectedJedinice[0] as any)[
        'en~jedinica_gradiva~razina'
      ];
      const firstParent =
        selectedJedinice[0]['en~jedinica_gradiva~nadredena_jedinica_gradiva'];
      const allSameRazina = selectedJedinice.every(
        (j: any) => j['en~jedinica_gradiva~razina'] === firstRazina
      );
      const allSameParent = selectedJedinice.every(
        (j) =>
          j['en~jedinica_gradiva~nadredena_jedinica_gradiva'] === firstParent
      );
      if (!allSameRazina) {
        return {
          disabled: true,
          message: 'Premještanje je moguće samo za jedinice iste razine.'
        };
      }
      if (!allSameParent) {
        return {
          disabled: true,
          message:
            'Premještanje je moguće samo za jedinice iz iste nadređene jedinice.'
        };
      }
    }

    const isDifferent = isDifferentInstitution(
      selectedJedinice,
      user as AppUser
    );

    if (isDifferent) {
      return { disabled: true, message: '' }; // TODO Ali cemo u nekom trenutku ipak morati dopustiti ako korisnik radi u ime druge institucije
    } else if (AuthService.userIsAdminOrVoditeljZbirki(user as AppUser)) {
      return { disabled: false, message: '' };
    } else {
      const mapped = selectedJedinice
        .map((item: JedinicaGradiva) => {
          const disabledState = isActionOnJedinicaDisabled(
            item,
            user as AppUser
          );
          return disabledState;
        })
        .filter((j) => j.disabled);
      if (mapped.length > 0) {
        return mapped[0];
      }
    }
  }
  return { disabled: false, message: '' };
}

export function isActionOnJedinicaDisabled(
  item: any,
  user: AppUser
): { disabled: boolean; message: string } {
  const arhivistiIds =
    ((item as JedinicaGradiva)[
      'en~jedinica_gradiva~arhivist_na_obradi'
    ] as number[]) || [];
  const voditeljiIds =
    ((item as JedinicaGradiva)[
      'en~jedinica_gradiva~voditelj_obrade'
    ] as number[]) || [];
  const locked =
    ((item as JedinicaGradiva)['en~jedinica_gradiva~locked'] as boolean) ||
    false;
  const residual =
    (item as JedinicaGradiva)['en~jedinica_gradiva~rezidualni_podaci'] || false;
  const nerazvrstanoZbirka = isDisabledActionForNerazvrstano([item]);
  const result = { disabled: false, message: '' };
  if (locked) {
    result.message = 'Jedinica gradiva je zaključana za uređivanje.';
    result.disabled = locked;
  } else if (residual) {
    result.message = 'Jedinica gradiva je rezidualno obrisana';
    result.disabled = residual;
  } else if (nerazvrstanoZbirka) {
    result.message =
      'Na grani nerazvrstanog gradiva nije moguće izvoditi akciju.';
    result.disabled = nerazvrstanoZbirka;
  } else {
    result.disabled =
      !AuthService.userIsAdminOrVoditeljZbirki(user) &&
      arhivistiIds.indexOf(user.userId?.value) === -1 &&
      voditeljiIds.indexOf(user.userId?.value) === -1;

    result.message = result.disabled
      ? 'Nemate prava uređivanja gradiva. Kontaktirajte zaduženog voditelja za dodjelu prava.'
      : '';
  }
  return result;
}

export function calculateDisabledStateAndTooltipForSelectedJedinice(
  selectedItems: object[]
): [boolean, string] {
  let nonPublicSelected = false;
  let nonVerifiedSelected = false;
  let rezidualnoSelected = false;
  let tooltip = '';
  const allSelectedPublisheable = selectedItems.every((item: any) => {
    const jedinicaGradiva = item as JedinicaGradiva;
    nonPublicSelected =
      jedinicaGradiva['en~jedinica_gradiva~status_dostupnosti'] !== 'JAVNO';
    nonVerifiedSelected =
      jedinicaGradiva['en~jedinica_gradiva~status_obrade'] !== 'VERIFICIRAN';
    rezidualnoSelected =
      jedinicaGradiva['en~jedinica_gradiva~rezidualni_podaci'] == true;
    if (nonPublicSelected) {
      tooltip = `Odabrali ste jedinicu ${jedinicaGradiva['en~jedinica_gradiva~arhivska_oznaka']} koja nije označena kao javno dostupna.`;
    }
    if (nonVerifiedSelected) {
      tooltip = `Odabrali ste jedinicu ${jedinicaGradiva['en~jedinica_gradiva~arhivska_oznaka']} koja nije verificirana.`;
    }
    if (rezidualnoSelected) {
      tooltip = `Odabrali ste jedinicu ${jedinicaGradiva['en~jedinica_gradiva~arhivska_oznaka']} koja je rezidualno obrisana.`;
    }
    return !nonPublicSelected && !nonVerifiedSelected && !rezidualnoSelected;
  });
  return [allSelectedPublisheable, tooltip];
}

export function calculateDisabledStateAndTooltipForSelectedJediniceDostave(
  selectedItems: object[]
): [boolean, string] {
  let rezidualnoSelected = false;
  let tooltip = '';
  const allSelectedDeliverable = selectedItems.every((item: any) => {
    const jedinicaGradiva = item as JedinicaGradiva;
    rezidualnoSelected =
      jedinicaGradiva['en~jedinica_gradiva~rezidualni_podaci'] == true;
    if (rezidualnoSelected) {
      tooltip = `Odabrali ste jedinicu ${jedinicaGradiva['en~jedinica_gradiva~arhivska_oznaka']} koja je rezidualno obrisana.`;
    }
    return !rezidualnoSelected;
  });
  return [allSelectedDeliverable, tooltip];
}
